import { useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Field, Input, Kpi, useToast } from '@w7/ui'
import { cmApi } from '@/api/cmApi'
import { useCmMe } from '@/api/hooks/useCmMe'
import { authHeaders } from '@/auth/session'
import {
  canCreateDownline,
  canCreditTransfer,
  canEditDownline,
  canViewCommission,
  canViewPlayerPnl,
} from '@/app/staffAccess'
import { CmPanel, CmStatus } from '@/features/catalog/CmPanel'
import { TransferDialog } from './TransferDialog'
import {
  ROLE_LABEL,
  ROLE_SHORT,
  bpToCommissionPercent,
  currencyLabel,
  displayToPaise,
  formatCasinoSharePoints,
  formatNodeMoney,
  formatSharePoints,
  idsEqual,
  isDirectChildOf,
  levelToRole,
  nodeSharePoints,
  parseCurrencyType,
  settleFromPostedPnl,
  treeStartParentId,
  type HierarchyNode,
} from './hierarchy'
import './hierarchy.css'
import { resetHierarchyDetailScroll } from './hierarchyDetailScroll'

function childrenOf(nodes: HierarchyNode[], parentId: string | null): HierarchyNode[] {
  if (parentId == null) return nodes.filter((n) => n.parentId == null)
  return nodes.filter((n) => idsEqual(n.parentId, parentId))
}

function TreeRows({
  nodes,
  parentId,
  depth,
  selectedId,
  editorRootId,
  allowEdit,
  allowViewCommission,
  onSelect,
  onEdit,
}: {
  nodes: HierarchyNode[]
  parentId: string | null
  depth: number
  selectedId: string | null
  editorRootId: string | null
  allowEdit: boolean
  allowViewCommission: boolean
  onSelect: (id: string) => void
  onEdit: (id: string) => void
}) {
  const kids = childrenOf(nodes, parentId)
  return (
    <>
      {kids.map((node) => {
        const role = levelToRole(node.level)
        const share = nodeSharePoints(node)
        const selected = node.id === selectedId
        const canEdit = allowEdit && isDirectChildOf(node, editorRootId, depth)
        return (
          <div key={node.id}>
            <div
              className={`cm-hier-line${selected ? ' is-selected' : ''}`}
              style={{ paddingLeft: 12 + depth * 16 }}
            >
              <span className="cm-hier-ident">
                <button
                  type="button"
                  className="cm-hier-row"
                  onClick={() => onSelect(node.id)}
                >
                  <span className="cm-hier-role" title={ROLE_LABEL[role]}>
                    {ROLE_SHORT[role]}
                  </span>
                  <strong className="cm-hier-name">{node.name}</strong>
                </button>
                {canEdit ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="primary"
                    className="cm-hier-edit"
                    onClick={() => onEdit(node.id)}
                  >
                    Edit
                  </Button>
                ) : null}
              </span>
              <button
                type="button"
                className="cm-hier-meta"
                onClick={() => onSelect(node.id)}
              >
                {formatNodeMoney(node.creditLimit, node.currencyType)} ·{' '}
                {formatSharePoints(share.sportsbook, parseCurrencyType(node.currencyType))} /{' '}
                {formatCasinoSharePoints(share.casino, parseCurrencyType(node.currencyType))}
                {allowViewCommission
                  ? ` · fancy ${bpToCommissionPercent(node.commissionBp ?? 0).toFixed(2)}% · BM ${bpToCommissionPercent(
                      node.commissionBookmakerBp ?? node.commissionBp ?? 0,
                    ).toFixed(2)}%`
                  : ''}
              </button>
            </div>
            <TreeRows
              nodes={nodes}
              parentId={node.id}
              depth={depth + 1}
              selectedId={selectedId}
              editorRootId={editorRootId}
              allowEdit={allowEdit}
              allowViewCommission={allowViewCommission}
              onSelect={onSelect}
              onEdit={onEdit}
            />
          </div>
        )
      })}
    </>
  )
}

export function HierarchyPage() {
  const navigate = useNavigate()
  const toast = useToast()
  const qc = useQueryClient()
  const me = useCmMe()
  const allowEditDownline = me.isSuccess && canEditDownline(me.data?.caller)
  const allowCreateDownline = me.isSuccess && canCreateDownline(me.data?.caller)
  const allowCreditTransfer = me.isSuccess && canCreditTransfer(me.data?.caller)
  const allowViewCommission = me.isSuccess && canViewCommission(me.data?.caller)
  const allowPlayerPnl = me.isSuccess && canViewPlayerPnl(me.data?.caller)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [giveAmount, setGiveAmount] = useState('')
  const [takeAmount, setTakeAmount] = useState('')
  const [transferDialog, setTransferDialog] = useState<{
    account: HierarchyNode
    direction: 'give' | 'take'
    amount?: number
  } | null>(null)
  const detailRef = useRef<HTMLDivElement>(null)

  const selectNode = (id: string) => {
    setSelectedId(id)
    // Sticky panel stays in view; reset its own scroll so the new node starts at top.
    requestAnimationFrame(() => {
      resetHierarchyDetailScroll(
        detailRef.current,
        window.matchMedia('(max-width: 900px)').matches,
      )
    })
  }

  const self = useQuery({
    queryKey: ['cm', 'hierarchy', 'self'],
    queryFn: () => cmApi.hierarchySelf(authHeaders()),
  })

  const tree = useQuery({
    queryKey: ['cm', 'hierarchy', 'tree'],
    queryFn: () => cmApi.hierarchyTree(authHeaders()),
  })

  const selected = useMemo(() => {
    const id = selectedId ?? self.data?.node.id ?? null
    if (!id || !tree.data?.nodes) return null
    return tree.data.nodes.find((n) => n.id === id) ?? null
  }, [selectedId, self.data?.node.id, tree.data?.nodes])

  const detail = useQuery({
    queryKey: ['cm', 'hierarchy', 'agent', selected?.id],
    queryFn: () => cmApi.hierarchyAgent(authHeaders(), selected!.id),
    enabled: !!selected?.id,
  })

  const transfer = useMutation({
    mutationFn: (input: { id: string; direction: 'give' | 'take'; amount: string }) =>
      cmApi.hierarchyCreditTransfer(authHeaders(), input, crypto.randomUUID()),
    onSuccess: () => {
      toast.success('Credit moved')
      void qc.invalidateQueries({ queryKey: ['cm', 'hierarchy'] })
      setGiveAmount('')
      setTakeAmount('')
    },
    onError: (err: Error) => toast.error(err.message),
  })

  const root = self.data?.node
  const nodes = tree.data?.nodes ?? []
  const detailNode = detail.data?.node ?? selected
  const detailMoney = detail.data?.money
  const detailShare = detailNode ? nodeSharePoints(detailNode) : null

  const isDirectChild = !!detailNode && isDirectChildOf(detailNode, root?.id)

  return (
    <CmPanel
      title="Portal hierarchy"
      subtitle="Full downline under Company Master — credit, share, fancy/BM commission (INR or POINTS)"
      actions={
        <>
          <Button type="button" variant="secondary" onClick={() => navigate('/hierarchy/list')}>
            Full list
          </Button>
          <Button type="button" variant="secondary" onClick={() => void tree.refetch()}>
            Refresh
          </Button>
          {allowCreateDownline ? (
            <Button type="button" variant="primary" onClick={() => navigate('/hierarchy/create')}>
              Create Triple Master
            </Button>
          ) : null}
        </>
      }
    >
      <div className="w7-grid w7-grid-4 cm-home-kpis" data-testid="cm-money-kpis">
        <Kpi
          label="Live balance"
          value={
            self.isLoading
              ? '—'
              : formatNodeMoney(
                  self.data?.money?.liveBalance ?? root?.liveBalance ?? root?.balance,
                  root?.currencyType,
                )
          }
        />
        <Kpi
          label="Total balance"
          value={
            self.isLoading
              ? '—'
              : formatNodeMoney(
                  self.data?.money?.totalBalance ?? root?.totalBalance ?? root?.creditLimit,
                  root?.currencyType,
                )
          }
        />
        <Kpi
          label="Distributed balance"
          value={
            self.isLoading
              ? '—'
              : formatNodeMoney(
                  self.data?.money?.distributedBalance ?? self.data?.allocatedCredit,
                  root?.currencyType,
                )
          }
        />
        <Kpi
          label="Open exposure"
          value={
            self.isLoading
              ? '—'
              : formatNodeMoney(
                  self.data?.money?.exposure ?? root?.exposure,
                  root?.currencyType,
                )
          }
        />
      </div>

      {self.isError || tree.isError ? (
        <CmStatus
          value={(self.error as Error)?.message || (tree.error as Error)?.message || 'Failed'}
          label="Could not load hierarchy"
        />
      ) : null}

      <p className="cm-hint">
        Currency is <strong>INR</strong> or <strong>POINTS</strong> (1 pt = ₹100; amounts on the wire
        are paise). Sportsbook and casino shares, fancy / bookmaker / match-odds commission, and
        game flags are stored on the hierarchy node.
      </p>

      <div className="cm-hier-layout">
        <div className="cm-hier-tree" data-testid="hierarchy-tree">
          <div className="cm-hier-tree-head">Downline tree</div>
          {tree.isLoading ? (
            <p className="cm-hint">Loading tree…</p>
          ) : tree.isError ? null : nodes.length === 0 ? (
            <p className="cm-hint">No nodes yet — create a Triple Master.</p>
          ) : (
            <TreeRows
              nodes={nodes}
              parentId={treeStartParentId(nodes, root?.id)}
              depth={0}
              selectedId={selected?.id ?? null}
              editorRootId={root?.id ?? null}
              allowEdit={allowEditDownline}
              allowViewCommission={allowViewCommission}
              onSelect={selectNode}
              onEdit={(id) => navigate(`/hierarchy/edit/${id}`)}
            />
          )}
        </div>

        <div
          ref={detailRef}
          className="cm-hier-detail"
          data-testid="hierarchy-detail"
        >
          <div className="cm-hier-tree-head">Selected node</div>
          {!detailNode ? (
            <p className="cm-hint">Select a node in the tree.</p>
          ) : (
            <>
              <dl className="cm-hier-dl">
                <div>
                  <dt>Name</dt>
                  <dd>{detailNode.name}</dd>
                </div>
                <div>
                  <dt>Role</dt>
                  <dd>{ROLE_LABEL[levelToRole(detailNode.level)]}</dd>
                </div>
                <div>
                  <dt>Status</dt>
                  <dd>{detailNode.status ?? 'active'}</dd>
                </div>
                <div>
                  <dt>Live balance</dt>
                  <dd>
                    {formatNodeMoney(
                      detailMoney?.liveBalance ?? detailNode.liveBalance ?? detailNode.balance,
                      detailNode.currencyType,
                    )}
                  </dd>
                </div>
                <div>
                  <dt>Total balance</dt>
                  <dd>
                    {formatNodeMoney(
                      detailMoney?.totalBalance ??
                        detailNode.totalBalance ??
                        detailNode.creditLimit,
                      detailNode.currencyType,
                    )}
                  </dd>
                </div>
                <div>
                  <dt>Distributed balance</dt>
                  <dd>
                    {formatNodeMoney(
                      detailMoney?.distributedBalance ?? detailNode.distributedBalance,
                      detailNode.currencyType,
                    )}
                  </dd>
                </div>
                <div>
                  <dt>Open exposure</dt>
                  <dd>
                    {formatNodeMoney(
                      detailMoney?.exposure ?? detailNode.exposure,
                      detailNode.currencyType,
                    )}
                  </dd>
                </div>
                <div>
                  <dt>Member Rate</dt>
                  <dd>
                    {formatSharePoints(
                      detailShare?.sportsbook ?? 0,
                      parseCurrencyType(detailNode.currencyType),
                    )}
                  </dd>
                </div>
                <div>
                  <dt>Member Rate (Casino)</dt>
                  <dd>
                    {formatCasinoSharePoints(
                      detailShare?.casino ?? 0,
                      parseCurrencyType(detailNode.currencyType),
                    )}
                  </dd>
                </div>
                {allowViewCommission ? (
                  <div>
                    <dt>Fancy / BM / MO</dt>
                    <dd>
                      {bpToCommissionPercent(detailNode.commissionBp ?? 0).toFixed(2)}% ·{' '}
                      {bpToCommissionPercent(
                        detailNode.commissionBookmakerBp ?? detailNode.commissionBp ?? 0,
                      ).toFixed(2)}
                      % ·{' '}
                      {bpToCommissionPercent(
                        detailNode.commissionMatchOddsBp ?? detailNode.commissionBp ?? 0,
                      ).toFixed(2)}
                      %
                    </dd>
                  </div>
                ) : null}
                {detailNode.games ? (
                  <div>
                    <dt>Games</dt>
                    <dd>
                      {[
                        detailNode.games.sportsbook !== false ? 'SB' : null,
                        detailNode.games.casino !== false ? 'Casino' : null,
                        detailNode.games.cardGame !== false ? 'Card' : null,
                        detailNode.games.liveGame !== false ? 'Live' : null,
                        detailNode.games.premiumSportsbook ? 'Premium' : null,
                      ]
                        .filter(Boolean)
                        .join(' · ') || '—'}
                    </dd>
                  </div>
                ) : null}
              </dl>

              {detailNode ? (
                <div className="cm-hier-detail-actions">
                  {allowPlayerPnl ? (
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => navigate(`/hierarchy/statement/${detailNode.id}`)}
                    >
                      View statement
                    </Button>
                  ) : null}
                  {isDirectChild && allowEditDownline ? (
                    <Button
                      type="button"
                      variant="primary"
                      onClick={() => navigate(`/hierarchy/edit/${detailNode.id}`)}
                    >
                      Edit downline
                    </Button>
                  ) : null}
                </div>
              ) : null}

              {isDirectChild && allowCreditTransfer ? (
                <div className="cm-hier-transfer">
                  {(() => {
                    const outstanding = settleFromPostedPnl(
                      detailNode.postedPnl,
                      detailNode.currencyType,
                    )
                    if (!outstanding) return null
                    const label = formatNodeMoney(outstanding.amountMinor, detailNode.currencyType)
                    return (
                      <div className="cm-hier-settle">
                        <p className="cm-hint">
                          Outstanding {outstanding.direction === 'take' ? 'Take' : 'Give'}:{' '}
                          <strong>{label}</strong> (Lab balanceUp — settle back toward credit limit)
                        </p>
                        <Button
                          type="button"
                          variant="primary"
                          onClick={() =>
                            setTransferDialog({
                              account: detailNode,
                              direction: outstanding.direction,
                              amount: outstanding.amountDisplay,
                            })
                          }
                        >
                          Settle {outstanding.direction === 'take' ? 'Take' : 'Give'} {label}
                        </Button>
                      </div>
                    )
                  })()}
                  <Field label={`Give (${currencyLabel(parseCurrencyType(detailNode.currencyType))})`}>
                    {({ id, describedBy }) => (
                      <Input
                        id={id}
                        aria-describedby={describedBy}
                        value={giveAmount}
                        onChange={(e) => setGiveAmount(e.target.value)}
                        inputMode="decimal"
                        placeholder="0"
                      />
                    )}
                  </Field>
                  <Button
                    type="button"
                    variant="primary"
                    busy={transfer.isPending}
                    onClick={() => {
                      const n = Number(giveAmount)
                      if (!Number.isFinite(n) || n <= 0) {
                        toast.error('Enter a give amount')
                        return
                      }
                      transfer.mutate({
                        id: detailNode.id,
                        direction: 'give',
                        amount: displayToPaise(n, parseCurrencyType(detailNode.currencyType)),
                      })
                    }}
                  >
                    Give
                  </Button>
                  <Field label={`Take (${currencyLabel(parseCurrencyType(detailNode.currencyType))})`}>
                    {({ id, describedBy }) => (
                      <Input
                        id={id}
                        aria-describedby={describedBy}
                        value={takeAmount}
                        onChange={(e) => setTakeAmount(e.target.value)}
                        inputMode="decimal"
                        placeholder="0"
                      />
                    )}
                  </Field>
                  <Button
                    type="button"
                    variant="secondary"
                    busy={transfer.isPending}
                    onClick={() => {
                      const n = Number(takeAmount)
                      if (!Number.isFinite(n) || n <= 0) {
                        toast.error('Enter a take amount')
                        return
                      }
                      transfer.mutate({
                        id: detailNode.id,
                        direction: 'take',
                        amount: displayToPaise(n, parseCurrencyType(detailNode.currencyType)),
                      })
                    }}
                  >
                    Take
                  </Button>
                </div>
              ) : (
                <p className="cm-hint">
                  {isDirectChild
                    ? 'Give / Take requires the Give/Take access grant.'
                    : 'Give / Take only works for direct children.'}
                </p>
              )}
            </>
          )}
        </div>
      </div>

      {transferDialog && root && isDirectChildOf(transferDialog.account, root.id) ? (
        <TransferDialog
          self={root}
          account={transferDialog.account}
          initialDirection={transferDialog.direction}
          initialAmount={transferDialog.amount}
          onClose={() => setTransferDialog(null)}
        />
      ) : null}
    </CmPanel>
  )
}
